// ---------------------------------------------------------------------------
// SOC-004 — Unified Content Calendar shared types + visual config
// ---------------------------------------------------------------------------
// Mirrors the backend discriminated union at
// `workers/api-crm/src/routes/content-calendar-unified.ts` verbatim. Visual
// config maps every type to a Stero brand token (electric blue, power purple,
// action orange, success, foreground) — no hardcoded hex.
// ---------------------------------------------------------------------------

import {
  CalendarDays,
  Mail,
  PenSquare,
  Share2,
  Star,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export const UNIFIED_CALENDAR_TYPES = [
  "social",
  "blog",
  "email",
  "event",
  "review_request",
] as const;

export type UnifiedCalendarType = (typeof UNIFIED_CALENDAR_TYPES)[number];

export interface UnifiedCalendarItem {
  type: UnifiedCalendarType;
  id: string;
  date: string;
  tenantId: string;
  title: string;
  status: string;
  platform?: string;
  slug?: string;
  campaignId?: string;
  calendarId?: string;
  endDate?: string;
  subject?: string;
  channel?: string;
}

export interface UnifiedCalendarResponse {
  data: {
    items: UnifiedCalendarItem[];
    from: string;
    to: string;
    types: UnifiedCalendarType[];
  };
}

export interface CalendarTypeVisual {
  label: string;
  icon: LucideIcon;
  badgeClass: string;
  dotClass: string;
}

export const CALENDAR_TYPE_VISUALS: Record<UnifiedCalendarType, CalendarTypeVisual> = {
  social: {
    label: "Social",
    icon: Share2,
    badgeClass:
      "bg-[var(--color-primary)]/10 text-[var(--color-primary)] border-[var(--color-primary)]/30",
    dotClass: "bg-[var(--color-primary)]",
  },
  blog: {
    label: "Blog",
    icon: PenSquare,
    badgeClass:
      "bg-[color:color-mix(in_oklab,var(--color-secondary,#7C3AED)_12%,transparent)] text-[var(--color-secondary,#7C3AED)] border-[color:color-mix(in_oklab,var(--color-secondary,#7C3AED)_30%,transparent)]",
    dotClass: "bg-[var(--color-secondary,#7C3AED)]",
  },
  email: {
    label: "Email",
    icon: Mail,
    badgeClass:
      "bg-[color:color-mix(in_oklab,var(--color-accent,#F05E0A)_12%,transparent)] text-[var(--color-accent,#F05E0A)] border-[color:color-mix(in_oklab,var(--color-accent,#F05E0A)_30%,transparent)]",
    dotClass: "bg-[var(--color-accent,#F05E0A)]",
  },
  event: {
    label: "Event",
    icon: CalendarDays,
    badgeClass:
      "bg-[var(--color-success)]/10 text-[var(--color-success)] border-[var(--color-success)]/30",
    dotClass: "bg-[var(--color-success)]",
  },
  review_request: {
    label: "Review request",
    icon: Star,
    badgeClass:
      "bg-[var(--color-muted)] text-[var(--color-foreground)] border-[var(--color-border)]",
    dotClass: "bg-[var(--color-foreground)]",
  },
};

export function formatItemDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function describeCalendarItem(item: UnifiedCalendarItem): string | null {
  switch (item.type) {
    case "social":
      return item.platform ? `Platform: ${item.platform}` : null;
    case "blog":
      return item.slug ? `Slug: ${item.slug}` : null;
    case "email":
      return item.subject ? `Subject: ${item.subject}` : null;
    case "event":
      return item.endDate ? `Ends ${formatItemDate(item.endDate)}` : null;
    case "review_request":
      return item.channel ? `Channel: ${item.channel}` : null;
    default:
      return null;
  }
}

export function defaultCalendarRange(): { from: string; to: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from: start.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
  };
}

export function dateInputToIsoStart(value: string): string {
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

export function dateInputToIsoEnd(value: string): string {
  return new Date(`${value}T23:59:59.999Z`).toISOString();
}
